import type { FleetUser, VehicleDriverAuthorizationStatus, VehicleDto, VehicleFragment, VehicleOdometer, VehicleTag } from '@cobliteam/api-lib/src/__generated__/graphql';

import type { User } from '@cobliteam/common-types';
import type { UUID } from './identifier';

export enum VehicleType {
  // General
  CAR = 'CAR',
  VAN = 'VAN',
  TRUCK = 'TRUCK',
  MOTORCYCLE = 'MOTORCYCLE',
  BUS = 'BUS',
  PICKUP = 'PICKUP',

  // Services
  AMBULANCE = 'AMBULANCE',
  FIRE_TRUCK = 'FIRE_TRUCK',
  TAXI = 'TAXI',
  GARBAGE_TRUCK = 'GARBAGE_TRUCK',

  // Water
  BOAT = 'BOAT',

  // Building
  CRANE = 'CRANE',

  // Airport
  TRACTOR = 'TRACTOR',
  TUG = 'TUG',
  LOADER = 'LOADER',
  LADDER = 'LADDER',
  GPU_APU = 'GPU_APU',
  CONVEYOR = 'CONVEYOR',
  FORK_LIFT = 'FORK_LIFT',

  OTHER = 'OTHER',
}

export enum VehicleDriverAuthorizationMode {
  AllowAnyFleetDriver = 'ALLOW_ANY_FLEET_DRIVER',
  AllowChosenDriver = 'ALLOW_CHOSEN_DRIVER',
  Disabled = 'DISABLED',
}

export interface VehicleDriver {
  id: string;
  name: string;
  phoneNumbers: string[];
}

export interface VehicleLastLocation {
  isPlugged?: boolean;
  timeInMillis?: number;
  cobliIngestedAt?: number;
}
export interface VehicleModel {
  type?: VehicleType;
  brand?: string;
  name?: string;
  year?: string;
  version?: string;
  isSuggestion?: boolean;
}

export interface Fuel {
  type?: string;
  price?: number;
  consumption?: number;
}

export interface Vehicle {
  id: string;
  deviceId?: string;
  deviceType?: string;
  cobliId?: string;
  licensePlate?: string;
  driver?: VehicleDriver;
  driverId?: string;
  newDriver?: VehicleDriver;
  oldDriver?: VehicleDriver;
  model: VehicleModel;
  label: string;
  fleetId: string;
  tagId?: string;
  fuel?: Fuel;
  size?: string;
  fuelConsumption?: number;
  groups?: VehicleGroup[];
  speedLimitInKmh?: number;
  odometer?: VehicleOdometer;
  lastLocation?: VehicleLastLocation;
  disablerStatus?: VehicleDisablerStatus;
  isAllowListEnabled?: boolean;
  isImmobilizerEnabled?: boolean;
  vehicleDriverAuthorizationMode?: VehicleDriverAuthorizationMode;
  tags?: VehicleTag[];
  groupPath?: string;
  immobilizerStatus?: VehicleDriverAuthorizationStatus;
}

export interface VehicleGroup {
  id: UUID;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: UUID;
  creator?: User;
  vehicleIds?: UUID[];
  userIds?: UUID[];
  path?: string;
}

export interface VehicleGroupWithPath {
  id: UUID;
  name: string;
  path: string;
}

export interface VehicleGroupWithUsers {
  id: UUID;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  creatorId?: UUID;
  creator?: FleetUser;
  path?: string;
  vehicles?: VehicleDto[];
  users?: FleetUser[];
}

export interface NewVehicleGroup {
  name: string;
  vehicleIds: UUID[];
  userIds: UUID[];
}

export enum VehicleSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
}

export enum VehicleStatus {
  ON = 'on',
  OFF = 'off',
  DISABLED = 'disabled',
  DISABLE_PENDING = 'disable_pending',
  ENABLE_PENDING = 'enable_pending',
  REMOVED = 'removed',
  IN_DEVOLUTION = 'in_devolution',
}

export interface VehicleDisablerStatus {
  status?: string;
  lastDisablingAction?: string;
  lastDisablingActionTime?: number;
}

export enum MultiSelectVehicleAssociationType {
  MOVING_WITHOUT_VEHICLE = 'Perdendo dados',
  ASSOCIATED_TO_VEHICLE = 'Associado',
  NOT_ASSOCIATED_TO_VEHICLE = 'Desassociado',
}

export const getVehicleLabel = (licensePlate?: string, model?: string, brand?: string, cobliId?: string) => {
  return `${licensePlate || ''} ${brand || ''} ${model || ''}`.trim() || cobliId || 'Sem dispositivo';
};

export const getLicensePlateOrCobliIdOrPlaceholder = (vehicle: Vehicle) => {
  return vehicle.licensePlate || vehicle.cobliId || 'Sem dispositivo';
};

export const getVehicleGroupsLabel = (vehicle: Vehicle | VehicleFragment | undefined | null) => {
  if (!vehicle?.groups || vehicle.groups.length === 0) return 'Sem grupos cadastrados';

  return vehicle.groups.map((group) => group.name).join(', ');
};

export const parseVehicleSize = (size?: VehicleSize) => {
  switch (size?.toLowerCase()) {
    case VehicleSize.SMALL:
      return 'Pequeno';
    case VehicleSize.MEDIUM:
      return 'Médio';
    case VehicleSize.BIG:
      return 'Grande';
    default:
      return '---';
  }
};
